import type { SVGProps } from "react";

// Line-icons in a single-weight, rounded-cap grammar (DESIGN.md §5).
// Custom set — deliberately not a stock icon pack (DESIGN.md).

const PATHS: Record<string, React.ReactNode> = {
  floor: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  orders: (
    <>
      <path d="M6 3h9l3 3v15H6z" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </>
  ),
  menu: (
    <>
      <path d="M4 6h16M4 12h16M4 18h10" />
    </>
  ),
  customers: (
    <>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" />
    </>
  ),
  shifts: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5l3.2 2" />
    </>
  ),
  reports: (
    <>
      <path d="M5 20V10M12 20V4M19 20v-7" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
    </>
  ),
  scanner: (
    <>
      <path d="M4 7V5h2M20 7V5h-2M4 17v2h2M20 17v2h-2" />
      <path d="M7 8v8M10 8v8M13 8v8M16 8v8" />
    </>
  ),
  // --- pack nav (POS-WEB-KIT): same single-weight rounded-cap grammar ---
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17M8 3v4M16 3v4" />
    </>
  ),
  rooms: (
    <>
      <path d="M4 20V8l8-4 8 4v12" />
      <path d="M9.5 20v-6h5v6" />
    </>
  ),
  housekeeping: (
    <>
      <path d="M9 3h6l1 6H8z" />
      <path d="M12 9v7M8.5 21h7l-1-5h-5z" />
    </>
  ),
  queue: (
    <>
      <path d="M4 6h16M4 12h16M4 18h9" />
      <circle cx="19.5" cy="18" r="1.6" />
    </>
  ),
  patients: (
    <>
      <rect x="4" y="3.5" width="16" height="17" rx="2" />
      <path d="M12 8.5v6M9 11.5h6" />
    </>
  ),
  samples: (
    <>
      <path d="M9 3h6M10.5 3v6l-3.6 8.4A2 2 0 0 0 8.7 20h6.6a2 2 0 0 0 1.8-2.6L13.5 9V3" />
      <path d="M8.6 14h6.8" />
    </>
  ),
  classes: (
    <>
      <circle cx="12" cy="5.5" r="2.2" />
      <path d="M12 8v6M8 20l4-6 4 6M7 11h10" />
    </>
  ),
  stock: (
    <>
      <path d="M3.5 7.5 12 3.5l8.5 4v9L12 20.5l-8.5-4z" />
      <path d="M3.5 7.5 12 11.5l8.5-4M12 11.5v9" />
    </>
  ),
  folio: (
    <>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9.5 8h5M9.5 12h5" />
    </>
  ),
  tick: <path d="M5 12.5 9.5 17 19 7.5" />,
  stylist: (
    <>
      <circle cx="7" cy="6.5" r="2.5" />
      <circle cx="7" cy="17.5" r="2.5" />
      <path d="M9 8 19 18M9 16 19 6" />
    </>
  ),
};

export function Icon({
  name,
  size = 22,
  ...props
}: { name: keyof typeof PATHS | string; size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {PATHS[name] ?? PATHS.menu}
    </svg>
  );
}

/** Indian veg / non-veg marker: square outline with a filled dot. */
/**
 * The FSSAI glyph — the one statutory exemption in the whole system (DESIGN.md §3).
 * India requires green for vegetarian and brown for non-vegetarian on packaged and
 * served food, so these two colours are fixed by law and answer to no design token
 * ramp; they carry their own names in globals.css so nobody "corrects" them later.
 * The square is 14px and it never shrinks, whatever the tile does.
 */
export function FssaiMark({ veg }: { veg: boolean }) {
  const c = veg ? "var(--color-fssai-veg)" : "var(--color-fssai-nonveg)";
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-label={veg ? "Vegetarian" : "Non-vegetarian"}>
      <rect x="1" y="1" width="12" height="12" rx="2" fill="none" stroke={c} strokeWidth="1.4" />
      <circle cx="7" cy="7" r="2.6" fill={c} />
    </svg>
  );
}
